import { useState } from "react";
import TencentMapPicker from "@/components/feature/TencentMapPicker";

export interface AddressFormData {
  label: string;
  labelIcon: string;
  name: string;
  gender: "mr" | "ms";
  phone: string;
  district: string;
  houseNum: string;
  isDefault: boolean;
  provinceId?: number | null;
  cityId?: number | null;
  districtId?: number | null;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  longitude?: number | null;
  latitude?: number | null;
}

interface AddressFormInnerProps {
  initial?: Partial<AddressFormData>;
  saving?: boolean;
  submitError?: string;
  onSave: (data: AddressFormData) => void;
}

function AddressFormInner({ initial, saving = false, submitError = "", onSave }: AddressFormInnerProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [gender, setGender] = useState<"mr" | "ms">(initial?.gender ?? "mr");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [district, setDistrict] = useState(initial?.district ?? "");
  const [houseNum, setHouseNum] = useState(initial?.houseNum ?? "");
  const [isDefault, setIsDefault] = useState(initial?.isDefault ?? false);
  const [provinceId, setProvinceId] = useState<number | null>(initial?.provinceId ?? null);
  const [cityId, setCityId] = useState<number | null>(initial?.cityId ?? null);
  const [districtId, setDistrictId] = useState<number | null>(initial?.districtId ?? null);
  const [provinceName, setProvinceName] = useState(initial?.provinceName ?? "");
  const [cityName, setCityName] = useState(initial?.cityName ?? "");
  const [districtName, setDistrictName] = useState(initial?.districtName ?? "");
  const [latitude, setLatitude] = useState<number | null>(initial?.latitude ?? null);
  const [longitude, setLongitude] = useState<number | null>(initial?.longitude ?? null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showMapPicker, setShowMapPicker] = useState(false);

  const label = initial?.label ?? "家";
  const labelIcon = initial?.labelIcon ?? "ri-home-4-line";

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    if (!name.trim()) {
      nextErrors.name = "请填写联系人姓名";
    }
    if (!phone.trim()) {
      nextErrors.phone = "请填写手机号";
    }
    if (!district.trim()) {
      nextErrors.district = "请选择服务地址";
    }
    if (!houseNum.trim()) {
      nextErrors.houseNum = "请填写详细门牌号";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    if (saving || !validate()) {
      return;
    }

    onSave({
      label,
      labelIcon,
      name: name.trim(),
      gender,
      phone: phone.trim(),
      district: district.trim(),
      houseNum: houseNum.trim(),
      isDefault,
      provinceId,
      cityId,
      districtId,
      provinceName: provinceName.trim(),
      cityName: cityName.trim(),
      districtName: districtName.trim(),
      longitude,
      latitude,
    });
  };

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
          <div>
            <p className="mb-2.5 px-1 text-xs font-semibold" style={{ color: "#999", letterSpacing: "0.06em" }}>
              联系人信息
            </p>
            <div className="overflow-hidden rounded-2xl" style={{ background: "#fff", border: "1px solid #EEEAE4" }}>
              <div className="px-4 pt-4 pb-3" style={{ borderBottom: "1px solid #F5F2EE" }}>
                <p className="mb-1.5 text-xs font-medium" style={{ color: "#AAAAAA" }}>姓名</p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="请输入联系人姓名"
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                    style={{ color: "#1A1A1A" }}
                  />
                  <div className="flex w-full flex-shrink-0 items-center gap-2 sm:w-auto sm:gap-1.5">
                    <button
                      onClick={() => setGender("mr")}
                      className="flex-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold cursor-pointer transition-all sm:flex-none"
                      style={{
                        background: gender === "mr" ? "linear-gradient(135deg, #FF7A30, #FF5000)" : "#F5F2EE",
                        color: gender === "mr" ? "#fff" : "#888",
                      }}
                    >
                      先生
                    </button>
                    <button
                      onClick={() => setGender("ms")}
                      className="flex-1 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold cursor-pointer transition-all sm:flex-none"
                      style={{
                        background: gender === "ms" ? "linear-gradient(135deg, #FF7A30, #FF5000)" : "#F5F2EE",
                        color: gender === "ms" ? "#fff" : "#888",
                      }}
                    >
                      女士
                    </button>
                  </div>
                </div>
                {errors.name ? (
                  <p className="mt-1.5 flex items-center gap-1 text-[11px]" style={{ color: "#FF5C00" }}>
                    <i className="ri-error-warning-line text-xs" />
                    {errors.name}
                  </p>
                ) : null}
              </div>

              <div className="px-4 pt-4 pb-3">
                <p className="mb-1.5 text-xs font-medium" style={{ color: "#AAAAAA" }}>手机号</p>
                <div className="flex items-center gap-2">
                  <div
                    className="flex flex-shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5"
                    style={{ background: "#F5F2EE" }}
                  >
                    <i className="ri-smartphone-line text-sm" style={{ color: "#999" }} />
                    <span className="text-sm font-medium" style={{ color: "#555" }}>+86</span>
                  </div>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="请输入手机号"
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                    style={{ color: "#1A1A1A" }}
                  />
                  {phone ? (
                    <button onClick={() => setPhone("")} className="flex h-5 w-5 flex-shrink-0 items-center justify-center cursor-pointer">
                      <i className="ri-close-circle-fill text-base" style={{ color: "#CCCCCC" }} />
                    </button>
                  ) : null}
                </div>
                {errors.phone ? (
                  <p className="mt-1.5 flex items-center gap-1 text-[11px]" style={{ color: "#FF5C00" }}>
                    <i className="ri-error-warning-line text-xs" />
                    {errors.phone}
                  </p>
                ) : null}
                <p className="mt-1.5 flex items-center gap-1 text-[11px]" style={{ color: "#FFAA66" }}>
                  <i className="ri-information-line text-xs" />
                  请确认手机号可以联系到你
                </p>
              </div>
            </div>
          </div>

          <div>
            <p className="mb-2.5 px-1 text-xs font-semibold" style={{ color: "#999", letterSpacing: "0.06em" }}>
              上门地址
            </p>
            <div className="overflow-hidden rounded-2xl" style={{ background: "#fff", border: "1px solid #EEEAE4" }}>
              <button
                onClick={() => setShowMapPicker(true)}
                className="w-full px-4 pt-4 pb-3 text-left cursor-pointer"
                style={{ borderBottom: "1px solid #F5F2EE" }}
              >
                <p className="mb-1.5 text-xs font-medium" style={{ color: "#AAAAAA" }}>所在小区 / 大厦</p>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                    <i className="ri-map-pin-line text-base" style={{ color: district ? "#FF6018" : "#CCCCCC" }} />
                  </div>
                  <span className="min-w-0 flex-1 text-sm" style={{ color: district ? "#1A1A1A" : "#CCCCCC" }}>
                    {district || "点击选择服务地址"}
                  </span>
                  <div
                    className="ml-auto flex flex-shrink-0 items-center gap-1 rounded-lg px-2.5 py-1"
                    style={{ background: "#FFF2E8" }}
                  >
                    <i className="ri-map-2-line text-xs" style={{ color: "#FF6018" }} />
                    <span className="whitespace-nowrap text-xs font-medium" style={{ color: "#FF6018" }}>
                      地图选点
                    </span>
                  </div>
                </div>
                {errors.district ? (
                  <p className="mt-1.5 flex items-center gap-1 text-[11px]" style={{ color: "#FF5C00" }}>
                    <i className="ri-error-warning-line text-xs" />
                    {errors.district}
                  </p>
                ) : null}
              </button>

              <div className="px-4 pt-4 pb-3">
                <p className="mb-1.5 text-xs font-medium" style={{ color: "#AAAAAA" }}>详细门牌号</p>
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                    <i className="ri-door-line text-base" style={{ color: houseNum ? "#FF6018" : "#CCCCCC" }} />
                  </div>
                  <input
                    type="text"
                    value={houseNum}
                    onChange={(event) => setHouseNum(event.target.value)}
                    placeholder="如：9 号楼 3 单元 601 室"
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                    style={{ color: "#1A1A1A" }}
                  />
                </div>
                {errors.houseNum ? (
                  <p className="mt-1.5 flex items-center gap-1 text-[11px]" style={{ color: "#FF5C00" }}>
                    <i className="ri-error-warning-line text-xs" />
                    {errors.houseNum}
                  </p>
                ) : null}
              </div>
            </div>

            {latitude !== null && longitude !== null ? (
              <div className="mt-2 hidden rounded-xl px-3 py-2 text-[11px]" style={{ background: "#FFF8F2", color: "#C87546" }}>
                已绑定地图坐标：{latitude.toFixed(6)}, {longitude.toFixed(6)}
              </div>
            ) : null}
          </div>

          <div
            className="flex items-center justify-between rounded-2xl px-4 py-4 cursor-pointer"
            style={{ background: "#fff", border: "1px solid #EEEAE4" }}
            onClick={() => setIsDefault(!isDefault)}
          >
            <div className="flex items-center gap-3">
              <div
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                style={{ background: isDefault ? "#FFF2E8" : "#F5F2EE" }}
              >
                <i className="ri-bookmark-line text-base" style={{ color: isDefault ? "#FF6018" : "#AAAAAA" }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: "#1A1A1A" }}>设为默认地址</p>
                <p className="mt-0.5 text-xs" style={{ color: "#AAAAAA" }}>下次下单会优先使用这个地址</p>
              </div>
            </div>
            <div
              className="flex h-6 w-12 flex-shrink-0 rounded-full px-0.5 transition-all"
              style={{ background: isDefault ? "linear-gradient(90deg, #FF7A30, #FF5000)" : "#E0D8D0" }}
            >
              <div
                className="h-5 w-5 rounded-full bg-white transition-all"
                style={{
                  transform: isDefault ? "translateX(24px)" : "translateX(0)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                }}
              />
            </div>
          </div>

          {submitError ? (
            <div
              className="flex items-start gap-2 rounded-2xl px-4 py-3"
              style={{ background: "#FFF5F5", border: "1px solid #FFD8D8", color: "#D25B5B" }}
            >
              <i className="ri-error-warning-line mt-0.5 text-sm" />
              <p className="text-xs leading-relaxed">{submitError}</p>
            </div>
          ) : null}

          <div className="h-2" />
        </div>

        <div className="px-4 py-4 flex-shrink-0" style={{ borderTop: "1px solid #F0EAE0", background: "#fff" }}>
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full whitespace-nowrap rounded-full py-4 text-base font-bold text-white cursor-pointer disabled:cursor-not-allowed"
            style={{
              background: saving ? "#F0B37D" : "linear-gradient(135deg, #FF7A30 0%, #FF5000 100%)",
              opacity: saving ? 0.8 : 1,
            }}
          >
            {saving ? "保存中..." : "保存并使用"}
          </button>
        </div>
      </div>

      {showMapPicker ? (
        <TencentMapPicker
          initialValue={{
            address: district,
            latitude,
            longitude,
          }}
          onConfirm={(value) => {
            setDistrict(value.address);
            setProvinceId(null);
            setCityId(null);
            setDistrictId(null);
            setProvinceName(value.province ?? "");
            setCityName(value.city ?? "");
            setDistrictName(value.district ?? "");
            setLatitude(value.latitude);
            setLongitude(value.longitude);
            setShowMapPicker(false);
          }}
          onBack={() => setShowMapPicker(false)}
        />
      ) : null}
    </>
  );
}

interface AddressFormOverlayProps {
  title?: string;
  initial?: Partial<AddressFormData>;
  saving?: boolean;
  submitError?: string;
  onSave: (data: AddressFormData) => void;
  onClose: () => void;
}

export default function AddressFormOverlay({
  title = "新增地址",
  initial,
  saving = false,
  submitError = "",
  onSave,
  onClose,
}: AddressFormOverlayProps) {
  return (
    <div
      className="customer-fixed-shell fixed inset-y-0 z-[200] flex w-full flex-col"
      style={{ background: "#F7F5F2" }}
    >
      <div
        className="flex flex-shrink-0 items-center gap-3 px-4 pb-4"
        style={{
          background: "#fff",
          borderBottom: "1px solid #F0EAE0",
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
        }}
      >
        <button
          onClick={onClose}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full cursor-pointer"
          style={{ background: "#F5F2EE" }}
        >
          <i className="ri-arrow-left-s-line text-lg" style={{ color: "#555" }} />
        </button>
        <div className="flex-1 text-center">
          <h2 className="text-base font-bold" style={{ color: "#1A1A1A" }}>{title}</h2>
          <p className="mt-0.5 text-[11px]" style={{ color: "#BBBBBB" }}>填写上门服务地址信息</p>
        </div>
        <div className="w-9" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <AddressFormInner
          initial={initial}
          saving={saving}
          submitError={submitError}
          onSave={onSave}
        />
      </div>
    </div>
  );
}
